import { useState, useEffect, useCallback } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { DocumentPreviewTable } from '@/components/documents/DocumentPreviewTable'
import { TypedConfirmDialog } from '@/components/ui/TypedConfirmDialog'
import { useDocumentPreview, useBulkDelete } from '@/hooks/useDocumentManagement'
import { COLLECTION_METADATA, AGE_FILTER_PRESETS } from '@/lib/collections'
import { AlertTriangle, Database, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function DocumentManagementPage() {
  const { t } = useTranslation()
  const [selectedCollection, setSelectedCollection] = useState<string>('')
  const [ageFilter, setAgeFilter] = useState<string>('90')
  const [customDays, setCustomDays] = useState<string>('')
  const [excludeActive, setExcludeActive] = useState<boolean>(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showConfirm, setShowConfirm] = useState<boolean>(false)

  const { documents, loading: previewLoading, error: previewError, fetchDocuments } = useDocumentPreview()
  const { deleteDocuments, loading: deleteLoading } = useBulkDelete()

  const selectedMetadata = COLLECTION_METADATA.find((c) => c.name === selectedCollection)

  const effectiveDays = ageFilter === 'custom' ? parseInt(customDays) || 0 : parseInt(ageFilter)

  const refreshPreview = useCallback(() => {
    if (selectedCollection && effectiveDays > 0) {
      fetchDocuments({
        collection: selectedCollection,
        olderThanDays: effectiveDays,
        excludeActive,
      })
    }
  }, [selectedCollection, effectiveDays, excludeActive, fetchDocuments])

  useEffect(() => {
    refreshPreview()
  }, [refreshPreview])

  const handleDeleteClick = () => {
    if (selectedIds.size === 0) {
      alert(t('documents.alerts.selectAtLeastOne'))
      return
    }
    setShowConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedCollection || selectedIds.size === 0) return

    try {
      const result = await deleteDocuments(selectedCollection, Array.from(selectedIds))
      if (result.success) {
        alert(t('documents.alerts.deleted', { count: result.deletedCount }))
        setSelectedIds(new Set())
        setShowConfirm(false)
      } else {
        alert(
          t('documents.alerts.partial', {
            succeeded: result.deletedCount,
            failed: result.failedCount,
          })
        )
      }
      refreshPreview()
    } catch (error) {
      console.error('Deletion error:', error)
    }
  }

  const canDelete = selectedMetadata?.canBulkDelete && selectedIds.size > 0

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Database className="w-8 h-8 shrink-0 text-gray-700" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('documents.title')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('documents.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-sm font-medium text-red-900">{t('documents.dangerZone')}</span>
        </div>
      </div>

      <Card>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">{t('documents.selectCollection')}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('documents.selectCollectionHint')}
            </p>
          </div>

          <div>
            <label htmlFor="collection-select" className="block text-sm font-medium text-gray-700 mb-2">
              {t('documents.collection')}
            </label>
            <select
              id="collection-select"
              value={selectedCollection}
              onChange={(e) => {
                setSelectedCollection(e.target.value)
                setSelectedIds(new Set())
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('documents.selectCollectionPlaceholder')}</option>
              {COLLECTION_METADATA.map((collection) => (
                <option key={collection.name} value={collection.name}>
                  {t(`documents.collections.${collection.name}.label`, { defaultValue: collection.name })}
                </option>
              ))}
            </select>
          </div>

          {selectedMetadata && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <span className="font-medium">{t('documents.about')}</span>{' '}
                {t(`documents.collections.${selectedMetadata.name}.description`, { defaultValue: '' })}
              </p>
              {!selectedMetadata.canBulkDelete && (
                <p className="text-sm text-red-700 mt-2 font-medium">
                  {t('documents.bulkDisabled')}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {selectedCollection && selectedMetadata?.canBulkDelete && (
        <>
          <Card>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">{t('documents.filterSettings')}</h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t('documents.filterHint')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="age-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('documents.documentAge')}
                  </label>
                  <select
                    id="age-filter"
                    value={ageFilter}
                    onChange={(e) => setAgeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {AGE_FILTER_PRESETS.map((preset) => (
                      <option key={preset} value={preset}>
                        {preset === 'custom'
                          ? t('documents.olderThanCustom')
                          : t('documents.olderThanDays', { count: Number(preset) })}
                      </option>
                    ))}
                  </select>
                </div>

                {ageFilter === 'custom' && (
                  <div>
                    <label htmlFor="custom-days" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('documents.customDays')}
                    </label>
                    <input
                      id="custom-days"
                      type="number"
                      min="1"
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      placeholder={t('documents.customDaysPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    id="exclude-active"
                    type="checkbox"
                    checked={excludeActive}
                    onChange={(e) => setExcludeActive(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="exclude-active" className="ml-2 text-sm text-gray-700">
                    {t('documents.excludeActive')}
                  </label>
                </div>
              </div>
            </div>
          </Card>

          {previewLoading && (
            <Card>
              <div className="text-center py-12">
                <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 mt-4">{t('documents.loading')}</p>
              </div>
            </Card>
          )}

          {previewError && (
            <Card>
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 font-medium">{t('documents.loadFailed')}</p>
                <p className="text-sm text-red-500 mt-1">{previewError}</p>
              </div>
            </Card>
          )}

          {!previewLoading && !previewError && documents.length > 0 && (
            <>
              <DocumentPreviewTable
                documents={documents}
                displayFields={selectedMetadata.displayFields}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              />

              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={handleDeleteClick}
                  disabled={!canDelete || deleteLoading}
                  loading={deleteLoading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('documents.deleteSelected', { count: selectedIds.size })}
                </Button>
              </div>
            </>
          )}

          {!previewLoading && !previewError && documents.length === 0 && selectedCollection && (
            <Card>
              <div className="text-center py-12">
                <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t('documents.noneFound')}</p>
              </div>
            </Card>
          )}
        </>
      )}

      <TypedConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmDelete}
        title={t('documents.confirmTitle')}
        message={t(selectedCollection === 'users' ? 'documents.confirmMessageUsers' : 'documents.confirmMessage')}
        documentCount={selectedIds.size}
        loading={deleteLoading}
      />
    </div>
  )
}
